/**
 * Welle 33 (2026-05-16) — Dashboard-Panel "Catalysts diese Woche".
 *
 * Zeigt die kommenden marktweiten Events (FOMC, CPI, PCE, NFP) der naechsten
 * 14 Tage. Damit weiss der User auf einen Blick wann Fed spricht oder
 * Inflations-Daten kommen — wichtigste Trigger fuer Wochen-Trades.
 *
 * Quelle: v5_catalysts (symbol IS NULL = marktweit). Daten kommen aus dem
 * Migration-Seed (44 Termine fuer 2026 hardcoded).
 */

import { useCallback, useMemo } from 'react';
import { CalendarClock, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDataState } from '@/hooks/use-data-state';
import { DataStateView } from '@/components/ui/data-state-view';
import { CARD, CARD_HEADER, CARD_HINT } from '@/lib/design-tokens';
// W36-U4 — Klartext-Tooltips fuer FOMC / CPI / PCE / NFP Badges
import { TermTooltip } from '@/components/ui/term-tooltip';
import {
  fetchUpcomingMarketCatalysts,
  EVENT_TYPE_LABEL,
  daysUntil,
  type Catalyst,
} from '@/lib/catalysts';

const IMPACT_STYLE = {
  high: 'border-red-700/50 bg-red-950/30 text-red-200',
  medium: 'border-amber-700/40 bg-amber-950/25 text-amber-200',
  low: 'border-zinc-700/50 bg-zinc-900/30 text-zinc-300',
};

const EVENT_BADGE: Record<string, string> = {
  fomc: 'bg-violet-900/50 text-violet-200 border-violet-700/50',
  cpi: 'bg-red-900/40 text-red-200 border-red-700/40',
  pce: 'bg-pink-900/40 text-pink-200 border-pink-700/40',
  unemployment: 'bg-orange-900/40 text-orange-200 border-orange-700/40',
  earnings: 'bg-emerald-900/40 text-emerald-200 border-emerald-700/40',
  dividend_ex: 'bg-blue-900/40 text-blue-200 border-blue-700/40',
};

function formatTime(t: string | null): string {
  if (!t) return '';
  return ` · ${t.slice(0, 5)} UTC`;
}

export function UpcomingCatalystsPanel({ daysAhead = 14 }: { daysAhead?: number }) {
  const queryFn = useCallback(
    (signal: AbortSignal) => fetchUpcomingMarketCatalysts(daysAhead, signal),
    [daysAhead]
  );
  const state = useDataState({
    queryKey: ['catalysts', 'market', daysAhead],
    queryFn,
    ttlSeconds: 60 * 30,
  });

  return (
    <section className={CARD}>
      <header className={cn(CARD_HEADER, 'mb-2')}>
        <CalendarClock className="size-4 text-cockpit-accent" />
        <span>Markt-Catalysts naechste {daysAhead}T</span>
        <span className="text-[10px] text-zinc-500 ml-auto font-normal">FOMC · CPI · PCE · NFP</span>
      </header>
      <p className={cn(CARD_HINT, 'mb-3')}>
        Die wichtigsten US-Wirtschaftstermine die in den naechsten Tagen
        Volatilitaet bringen koennen. <strong className="text-zinc-200">FOMC</strong> = Fed-Zinsentscheidung,
        <strong className="text-zinc-200"> CPI</strong> = Verbraucherpreise (Inflation),
        <strong className="text-zinc-200"> PCE</strong> = Fed-bevorzugter Inflations-Indikator,
        <strong className="text-zinc-200"> NFP</strong> = Arbeitsmarktbericht.
      </p>
      <DataStateView
        state={state.state}
        empty={
          <div className="rounded-md border border-zinc-800/60 bg-zinc-950/30 p-3 text-[11px] text-zinc-500 leading-snug">
            Keine Markt-Catalysts in den naechsten {daysAhead} Tagen.
          </div>
        }
      >
        {(catalysts) => <CatalystsList catalysts={catalysts ?? []} />}
      </DataStateView>
    </section>
  );
}

function CatalystsList({ catalysts }: { catalysts: Catalyst[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, Catalyst[]>();
    for (const c of catalysts) {
      const arr = map.get(c.catalyst_date) ?? [];
      arr.push(c);
      map.set(c.catalyst_date, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalysts]);

  if (grouped.length === 0) {
    return (
      <div className="text-[11px] text-zinc-500 leading-snug">
        Keine Markt-Catalysts in den naechsten Tagen.
      </div>
    );
  }

  return (
    <ul className="space-y-1.5">
      {grouped.map(([date, items]) => {
        const days = daysUntil(date);
        const dateLabel = new Date(date + 'T00:00:00Z').toLocaleDateString('de-DE', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
        });
        return (
          <li key={date} className="flex items-start gap-2">
            <div className="shrink-0 w-24 text-[11px] text-zinc-400 leading-snug">
              <div className="font-semibold text-zinc-200">{dateLabel}</div>
              <div className="text-[10px] text-zinc-500">
                {days === 0 ? 'heute' : days === 1 ? 'morgen' : `in ${days}T`}
              </div>
            </div>
            <div className="flex-1 flex flex-wrap gap-1.5">
              {items.map((c) => {
                const badge = EVENT_BADGE[c.event_type] ?? 'bg-zinc-800 text-zinc-300';
                return (
                  <span
                    key={c.id}
                    className={cn(
                      'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]',
                      IMPACT_STYLE[c.expected_impact]
                    )}
                    title={c.notes ?? c.title}
                  >
                    <span
                      className={cn(
                        'rounded px-1 py-0.5 text-[9px] font-semibold border',
                        badge
                      )}
                    >
                      {/* W36-U4 — Tooltip mit ausfuehrlicher Erklaerung pro Event-Type */}
                      {(() => {
                        const t = c.event_type;
                        const tooltipKey =
                          t === 'fomc' ? 'fomc'
                          : t === 'cpi' ? 'cpi'
                          : t === 'pce' ? 'pce'
                          : t === 'unemployment' ? 'nfp'
                          : t === 'earnings' ? 'earnings'
                          : null;
                        const label = EVENT_TYPE_LABEL[t];
                        return tooltipKey ? (
                          <TermTooltip term={tooltipKey} inline>{label}</TermTooltip>
                        ) : (
                          label
                        );
                      })()}
                    </span>
                    <span className="leading-snug">{c.title.replace(EVENT_TYPE_LABEL[c.event_type] + ' ', '').replace(EVENT_TYPE_LABEL[c.event_type], '')}{formatTime(c.catalyst_time)}</span>
                    {c.expected_impact === 'high' && <Sparkles className="size-2.5" />}
                  </span>
                );
              })}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
